// [PURPOSE]: Single source of truth for which localStorage keys are
//            user-scoped (i.e., must be cleared on logout / delete-account so
//            the next user on the same browser doesn't see the previous user's
//            character roster, party state, journal, etc.).
// [WHY]: Phase 2 audit S-12 (Batch 1.5) — logout() previously called only
//        signOut(auth) and left all user-scoped keys behind. On a shared
//        device (family tablet, kiosk) User A's data was visible to User B
//        on next sign-in. deleteAccount() had its own inline list which
//        drifted from the actual set of user-scoped keys.
// [INTEGRATION]: Used by AuthContext.logout() and AuthContext.deleteAccount().

const USER_SCOPED_STORAGE_KEYS = [
  // Character + game state
  'savedCharacters',
  'tabletopInventory_autosave',
  'tabletopInventory_settings',
  'tabletopInventory_recentItems',
  'tabletopInventory_favoriteItems',
  // Custom content + sources
  'customComponents',
  'tabletop_sources',
  // Tutorial state (persisted both locally and in Firebase)
  'tabletopInventory_tutorialState',
  // Party state
  'partyId',
  'partyName',
  // UI preferences (per-user; reset on logout for clean slate)
  'quickMenuPosition',
  'collapsedSpellLevels',
];

/**
 * Remove all user-scoped keys from the given storage. Errors on individual
 * removes are swallowed (storage may be disabled, full, or hostile).
 *
 * @param {object} [opts]
 * @param {Storage} [opts.storage] - defaults to window.localStorage
 * @returns {{cleared: string[], errors: string[]}}
 */
function clearUserScopedStorage({ storage } = {}) {
  const target = storage ?? (typeof window !== 'undefined' ? window.localStorage : null);
  const cleared = [];
  const errors = [];

  if (!target || typeof target.removeItem !== 'function') {
    return { cleared, errors };
  }

  for (const key of USER_SCOPED_STORAGE_KEYS) {
    try {
      target.removeItem(key);
      cleared.push(key);
    } catch (e) {
      errors.push(key);
    }
  }

  return { cleared, errors };
}

export { USER_SCOPED_STORAGE_KEYS, clearUserScopedStorage };
